import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMyEvents } from "@/hooks/useMyEvents";
import { MyEventCardVertical } from "./MyEventCardVertical";
import { MyEventCardHorizontal } from "./MyEventCardHorizontal";
import { useNavigate } from 'react-router-dom';

interface MyEventsProps {
  userId?: string;
  isOwnProfile?: boolean;
}

const MyEvents = ({ userId, isOwnProfile = true }: MyEventsProps) => {
  const { myEvents, loading } = useMyEvents(userId);
  const navigate = useNavigate();
  
  const sectionTitle = isOwnProfile ? "Mis Eventos" : "Historial de Eventos";
  const emptyTitle = isOwnProfile ? "No tienes eventos" : "Sin eventos registrados";
  const emptyMessage = isOwnProfile 
    ? "¡Regístrate en tu primer torneo o liga!" 
    : "Este usuario aún no ha participado en eventos";

  if (loading) {
    return (
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Trophy className="h-5 w-5 text-primary" />
            <span>Mis Eventos</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 overflow-hidden">
            {[1, 2, 3].map(i => (
              <div key={i} className="min-w-[150px] h-[180px] bg-muted rounded-lg animate-pulse flex-shrink-0" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const allEvents = [
    ...myEvents.active,
    ...myEvents.upcoming,
    ...myEvents.finished
  ];
  
  const totalEvents = allEvents.length;
  const displayEvents = allEvents.slice(0, 8);

  // For other profiles, keep simple horizontal card list
  if (!isOwnProfile) {
    const otherProfileEvents = allEvents.slice(0, 5);
    return (
      <Card className="shadow-md border-0">
        <CardHeader className="px-4 py-3">
          <CardTitle className="flex items-center justify-between text-base">
            <div className="flex items-center space-x-2">
              <Trophy className="h-4 w-4 text-primary" />
              <span>{sectionTitle}</span>
            </div>
            <Badge variant="secondary" className="text-[10px]">{totalEvents} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 py-2">
          {otherProfileEvents.length === 0 ? (
            <div className="text-center py-6">
              <Trophy className="h-10 w-10 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-base font-semibold mb-2">{emptyTitle}</h3>
              <p className="text-sm text-muted-foreground">{emptyMessage}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {otherProfileEvents.map((event) => (
                <MyEventCardHorizontal key={event.registrationId || event.id} event={event} isOwnProfile={false} />
              ))}
              {totalEvents > 5 && (
                <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
                  Ver Todos los Eventos ({totalEvents})
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border-0">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Trophy className="h-5 w-5 text-primary" />
            <span>{sectionTitle}</span>
          </div>
          <Badge variant="secondary">{totalEvents} total</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {displayEvents.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">{emptyTitle}</h3>
            <p className="text-sm text-muted-foreground mb-4">{emptyMessage}</p>
            <Button onClick={() => navigate('/')}>Explorar Eventos</Button>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
            {displayEvents.map((event) => (
              <MyEventCardVertical key={event.registrationId || event.id} event={event} />
            ))}

            {/* "Ver todo" card */}
            {totalEvents > 0 && (
              <Card
                className="min-w-[150px] max-w-[150px] flex-shrink-0 cursor-pointer hover:shadow-md transition-shadow border-border/50 flex items-center justify-center"
                onClick={() => navigate('/my-events')}
              >
                <div className="flex flex-col items-center gap-2 p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <ChevronRight className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-sm font-semibold">Ver todo</span>
                  <span className="text-xs text-muted-foreground">{totalEvents} eventos</span>
                </div>
              </Card>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyEvents;
